import { useState } from "react";
import { CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-ink/20 bg-cream px-4 py-3 text-sm outline-none placeholder:text-sand/70 focus:border-kwila";

const SUBJECTS = ["General enquiry", "Furniture order", "Timber supply", "Doors", "Freight / delivery", "Trade account"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Message sent", { description: "We will get back to you shortly." });
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="px-5 pb-16 pt-32 md:px-10 md:pt-44">
        <div className="mx-auto max-w-[1500px]">
          <Kicker>Contact</Kicker>
          <h1 className="mt-4 font-display text-5xl font-light leading-[0.95] tracking-tight md:text-7xl">
            <MaskedLine delay={0.1}>Talk timber</MaskedLine>
            <MaskedLine delay={0.22} className="italic text-kwila">with our team.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-12">
          <div className="space-y-10 lg:col-span-5">
            <Reveal>
              <p className="max-w-md text-base leading-relaxed text-ink/70">
                Retail orders, trade supply, freight quotes and project enquiries — send a message and the right
                person will come back to you.
              </p>
            </Reveal>
            {[
              { icon: MapPin, label: "Australia — Warehouse & Pickup", value: "Archerfield, Brisbane QLD (previous records: 66 Raynham St, Salisbury QLD 4107) [to be confirmed]" },
              { icon: MapPin, label: "PNG — Head Office", value: "Furniture Exports (PNG) Ltd, Montoro St, Lae, Morobe Province [to be confirmed]" },
              { icon: Phone, label: "Phone", value: "Australia (07) 3277 2166 · PNG (675) 472 4000 [from archived records — to be confirmed]" },
              { icon: Mail, label: "Email", value: "[To be confirmed by client]" },
            ].map((row, i) => (
              <Reveal key={row.label} delay={0.05 + i * 0.05} className="flex items-start gap-4 border-t border-ink/10 pt-6">
                <row.icon size={20} className="mt-1 text-kwila" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sand">{row.label}</p>
                  <p className="mt-1 font-display text-xl">{row.value}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="rounded-sm border border-ink/15 bg-stone/50 p-8 text-center" data-testid="map-placeholder">
                <p className="font-display text-2xl italic text-sand">Map to be added</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-sand">Archerfield warehouse location</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <Reveal className="flex h-full flex-col items-center justify-center rounded-sm border border-forest/30 bg-forest/5 p-12 text-center" data-testid="contact-success">
                <CheckCircle2 size={40} className="text-forest" />
                <p className="mt-4 font-display text-4xl">Message received.</p>
                <p className="mt-3 max-w-md text-sm text-ink/65">Thank you — our team will be in touch shortly.</p>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="rounded-sm border border-ink/15 bg-stone/40 p-6 md:p-10" data-testid="contact-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input data-testid="contact-name-input" required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} />
                    <input data-testid="contact-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                    <input data-testid="contact-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                    <select data-testid="contact-subject-select" value={form.subject} onChange={set("subject")} className={inputCls}>
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <textarea data-testid="contact-message-input" required rows={6} placeholder="How can we help? *" value={form.message} onChange={set("message")} className={`${inputCls} mt-4`} />
                  <button
                    data-testid="contact-submit-button"
                    disabled={busy}
                    className="mt-6 rounded-full bg-ink px-10 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila disabled:opacity-50"
                  >
                    {busy ? "Sending…" : "Send Message"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
